import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import ImageUploadFormR2 from '@/shared/ImageUploadFormR2'
import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'
import Loading from '@/shared/Loading'
import useModal from '@/hooks/useModalV2'
import Modal from '@/shared/Modal'
import { Link } from 'react-router-dom'

const NewCampaignPage = ({existingAd}) => {
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()
  const { openModal, closeModal, isOpen } = useModal()
  
  const [loading, setLoading] = useState(false)
  const [placementOptions, setPlacementOptions] = useState([])
  const [imageUrl, setImageUrl] = useState(null)
  const [imageUploaded, setImageUploaded] = useState(false)

  // Pricing comes from the server (Ad::PRICE_OPTIONS), including this buyer's
  // discount. This page used to hardcode the tiers, apply the 50% business
  // discount itself, and decide who qualified by checking gold === 'business' —
  // three copies of rules that live in the model and Acl. When they drifted,
  // the page quoted one price and Stripe charged another.
  const [pricingOptions, setPricingOptions] = useState([])
  const [hasBusinessDiscount, setHasBusinessDiscount] = useState(false)
  const [pricingLoaded, setPricingLoaded] = useState(false)

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      name: '',
      placement: 'square',
      url: ''
    }
  })

  const selectedPricingIndex = watch('impressions')
  const selectedPlacement = watch('placement')
  const adUrl = watch('url')

  useEffect(() => {
    // getRequest(uri, params, callback) — callback style, and `params` is
    // required: it goes straight into Object.keys().
    getRequest('/api/v3/ads/new', {}, (err, data) => {
      if (err || !data?.pricing_options) {
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
          <p className="mt-1 text-sm dark:text-red-300 text-red-400">
            Couldn't load our rate card. Refresh and we'll try to look professional again.
          </p>
        </div>)
        return
      }
      setPricingOptions(data.pricing_options)
      setHasBusinessDiscount(data.discounted)
      setPricingLoaded(true)
    })
  }, [])

  const handleImageUploadSuccess = () => {
    setImageUploaded(true)
    closeModal()
  }

  const onSubmit = async (data) => {
    if (!imageUrl) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">Please upload an image</p>
      </div>)
      return
    }

    setLoading(true)
    
    try {
      let action = 'POST'
      let url = '/api/v3/ads'

      if (existingAd?.id) {
        action = 'PATCH'
        url = `/api/v3/ads/${existingAd.id}`
      }
      const formData = {
        name: data.name,
        placement: data.placement,
        url: data.url,
        image_url: imageUrl,
        impressions: data.impressions,
      }
      
      putpostRequest(url, action, { ad: formData }, (err, jsonData) => {
        if (err) {
          setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
            <p className="mt-1 text-sm dark:text-red-300 text-red-400">{err.message || 'Failed to create ad'}</p>
          </div>)
          return
        }

        window.location.href = jsonData.stripeLink
      })
    } catch (error) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">{error} Error</p>
      </div>)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create New Campaign</h1>
          <p className="text-gray-600 dark:text-gray-300">Set up your advertising campaign</p>
        </div>
        <Link to="/advertising/campaigns">
          <DefaultButton onClick={() => {}} text="← Back to Campaigns" />
        </Link>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ad Name *
            </label>
            <input
              {...register('name', { required: 'Ad name is required' })}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-cccblue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter your ad name"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Ad Placement *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              { false && (
                <label className={`relative flex flex-col items-center p-6 border-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                  selectedPlacement === 'leaderboard' 
                    ? 'border-cccblue bg-cccblue bg-opacity-20 ring-2 ring-cccblue' 
                    : 'border-gray-200 dark:border-gray-600'
                }`}>
                  <input
                    {...register('placement', { required: 'Placement is required' })}
                    type="radio"
                    value="leaderboard"
                    className="sr-only"
                  />
                  <div className="w-full mb-4 flex justify-center">
                    <div className="bg-gradient-to-r from-cccblue-500 to-cccpurple-500 rounded shadow-sm border-2 border-gray-800 dark:border-gray-200 flex items-center justify-center text-gray-700 dark:text-white text-xs font-medium"
                         style={{ width: '182px', height: '22.5px' }}>
                      728 × 90
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Leaderboard</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">728 × 90 pixels</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Displays at the top of pages</p>
                  </div>
                  {selectedPlacement === 'leaderboard' && (
                    <div className="absolute top-2 right-2">
                      <div className="w-5 h-5 bg-cccblue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </label>
              )}

              <label className={`flex flex-col items-center p-6 border-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors relative ${
                selectedPlacement === 'square' 
                  ? 'border-cccorange bg-cccorange bg-opacity-20 ring-2 ring-cccorange' 
                  : 'border-gray-200 dark:border-gray-600'
              }`}>
                <input
                  {...register('placement')}
                  type="radio"
                  value="square"
                  className="sr-only"
                />
                <div className="w-full mb-4 flex justify-center">
                  <div className="bg-gradient-to-r from-cccorange-500 to-cccpurple-500 rounded shadow-sm flex border-2 border-gray-800 dark:border-gray-200 items-center justify-center text-gray-700 dark:text-white text-xs font-medium"
                       style={{ width: '70px', height: '70px' }}>
                    250 × 250
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Square</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">250 × 250 pixels</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Displays in sidebar areas</p>
                </div>
                {selectedPlacement === 'square' && (
                  <div className="absolute top-2 right-2">
                    <div className="w-5 h-5 bg-cccorange-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </label>
            </div>
            {errors.placement && <p className="text-red-500 text-sm mt-2">{errors.placement.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Image Upload *
            </label>
            {imageUrl ? (
              <div className="space-y-3">
                <div className="relative inline-block">
                  <img
                    src={imageUrl}
                    alt="Uploaded ad"
                    className="h-32 rounded border border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div>
                  <DefaultButton 
                    onClick={openModal} 
                    text="Change Image"
                    className="text-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                </svg>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  No image uploaded yet
                </p>
                <div className="flex justify-center">
                  <PrimaryButton 
                    onClick={openModal} 
                    text="Upload Image"
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target URL *
            </label>
            <input
              {...register('url', { 
                required: 'URL is required',
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Please enter a valid URL (starting with http:// or https://)'
                }
              })}
              type="url"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-cccblue-500 dark:bg-gray-700 dark:text-white"
              placeholder="https://example.com"
            />
            {errors.url && <p className="text-red-500 text-sm mt-1">{errors.url.message}</p>}
          </div>

          {(imageUrl || selectedPlacement) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Ad Preview
              </label>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 mb-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Preview of your {selectedPlacement} ad:
                  </p>
                  
                  <div className="flex justify-center">
                    {selectedPlacement === 'leaderboard' ? (
                      <div 
                        className="border-2 border-gray-300 dark:border-gray-600 rounded overflow-hidden bg-white dark:bg-gray-700"
                        style={{ width: '728px', height: '90px', maxWidth: '100%' }}
                      >
                        {imageUrl ? (
                          adUrl ? (
                            <a 
                              href={adUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="block w-full h-full hover:opacity-90 transition-opacity cursor-pointer"
                            >
                              <img
                                src={imageUrl}
                                alt="Ad preview"
                                className="w-full h-full object-cover"
                              />
                            </a>
                          ) : (
                            <img
                              src={imageUrl}
                              alt="Ad preview"
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700">
                            <div className="text-center">
                              <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                              </svg>
                              <p className="text-xs text-gray-500 dark:text-gray-400">728 × 90</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-gray-300 dark:border-gray-600 rounded overflow-hidden bg-white dark:bg-gray-700"
                        style={{ width: '250px', height: '250px' }}
                      >
                        {imageUrl ? (
                          adUrl ? (
                            <a 
                              href={adUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="block w-full h-full hover:opacity-90 transition-opacity cursor-pointer"
                            >
                              <img
                                src={imageUrl}
                                alt="Ad preview"
                                className="w-full h-full object-cover"
                              />
                            </a>
                          ) : (
                            <img
                              src={imageUrl}
                              alt="Ad preview"
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700">
                            <div className="text-center">
                              <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                              </svg>
                              <p className="text-sm text-gray-500 dark:text-gray-400">250 × 250</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                    {imageUrl ? (
                      adUrl ? (
                        <>
                          Your ad will display like this on the website.{' '}
                          <span className="text-cccblue-600 dark:text-cccblue-400">Click to test your link!</span>
                        </>
                      ) : (
                        'Your ad will display like this on the website. Add a URL to make it clickable.'
                      )
                    ) : (
                      'Upload an image to see your ad preview'
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {hasBusinessDiscount && (
            <div className="bg-gradient-to-r from-cccorange to-cccpurple dark:from-cccorange dark:to-cccpurple border border-cccorange dark:border-cccorange-800 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-white">
                    Business Plan Discount Active
                  </h3>
                  <p className="text-sm text-white mt-1">
                    You're receiving 50% off all advertising rates with your Business plan!
                  </p>
                </div>
              </div>
            </div>
          )}

          {!hasBusinessDiscount && (
            <div className="bg-gradient-to-r from-cccblue-50 to-cccpurple-50 dark:from-cccblue-900/20 dark:to-cccpurple-900/20 border border-cccblue dark:border-cccblue rounded-lg p-6 mb-6">
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="bg-gradient-to-r from-cccblue to-cccpurple rounded-full p-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Save 50% with Business Plan
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Upgrade to Business and get half-price advertising rates plus additional premium features.
                </p>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Your savings with Business Plan:</h4>
                  <div className="space-y-2 text-sm">
                    {pricingOptions.map((option, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400">
                          {option.impressions.toLocaleString()} impressions:
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 line-through">
                            ${option.list_cost.toFixed(2)}
                          </span>
                          <span className="font-bold text-cccorange dark:text-cccorange">
                            ${option.business_cost.toFixed(2)}
                          </span>
                          <span className="text-xs text-cccorange dark:text-cccorange px-2 py-1 rounded">
                            Save ${(option.list_cost - option.business_cost).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <a href="/membership"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cccblue to-cccpurple text-white text-sm font-medium rounded-md hover:from-cccblue hover:to-cccpurple transition-colors" >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Upgrade to Business Plan
                </a>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Pricing Options *
            </label>
            <div className="space-y-3">
              {!pricingLoaded && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Fetching today's rates. We promise they haven't changed since you clicked.
                </p>
              )}
              {pricingOptions.map((option, index) => (
                <label key={index} className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    {...register('impressions', { required: 'Please select a pricing option' })}
                    type="radio"
                    value={option.impressions}
                    className="text-cccblue-600 focus:ring-cccblue-500"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {option.impressions.toLocaleString()} Impressions
                      </span>
                      <div className="text-right">
                        {hasBusinessDiscount ? (
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                ${option.list_cost.toFixed(2)}
                              </span>
                              <span className="text-lg font-bold text-cccorange">
                                ${option.cost.toFixed(2)} USD
                              </span>
                            </div>
                            <span className="text-xs text-cccorange dark:text-cccorange font-medium">
                              50% OFF
                            </span>
                          </div>
                        ) : (
                          <span className="text-lg font-bold text-cccblue dark:text-cccblue">
                            ${option.cost.toFixed(2)} USD
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {hasBusinessDiscount ? (
                        <>
                          CPM: <span className="line-through text-gray-400">${option.list_cpm.toFixed(2)}</span>{' '}
                          <span className="text-cccorange dark:text-cccorange font-medium">
                            ${option.cpm.toFixed(2)}
                          </span>
                        </>
                      ) : (
                        `CPM: $${option.cpm.toFixed(2)}`
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {errors.impressions && <p className="text-red-500 text-sm mt-2">{errors.impressions.message}</p>}
          </div>


          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Payment Information</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Your payment method will not be charged until a moderator reviews and approves your ad campaign. You'll receive an email notification once your ad is approved and live.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <PrimaryButton
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              loading={loading}
              className="px-8"
              text="Continue to Payment"
              />
          </div>
        </form>
      </Card>

      <Modal
        isOpen={isOpen}
        closeModal={closeModal}
        title={`Upload ${selectedPlacement === 'leaderboard' ? 'Leaderboard' : 'Square'} Ad Image`}
        icon="image"
        maxWidth="max-w-2xl"
      >
        <div className="mt-6 flex justify-center">
          <ImageUploadFormR2
            kind="ad"
            url={imageUrl}
            setUrl={setImageUrl}
            success={handleImageUploadSuccess}
            didUpload={setImageUploaded}
            aspect={selectedPlacement === 'leaderboard' ? 'video' : 'square'}
          />
        </div>
      </Modal>
    </div>
  )
}

export default NewCampaignPage
